import { AxiosRequestConfig } from 'axios';
import {
  API_GET_ALL_STORES,
  API_GET_STORE_BY_ID,
  API_GET_USER_STORES,
  API_POST_UPSERT_STORE,
  API_SEARCH_STORES,
} from 'common/constants/apis';
import { CRASHLYTICS_AUTH_ERROR } from 'common/constants/errors';
import { AuthResp, ISearchCriteria, ServerResponse } from 'common/interfaces';
import { IStore, IStoreGetResp, IStoreListResp, IStoreUpdateResp } from 'common/interfaces/istore';
import UILogger, { LogRecordType, LogSeverityType } from 'common/utils/logger';
import { DEFAULT_RETRY_CONFIG } from './../common/constants/index';
import httpClient from './config';
import { IUserSearchCriteria } from 'common/interfaces/iuser';

export const getStoreById = (storeId: number): Promise<IStoreGetResp> => {
  UILogger.info('Get store by id: ', storeId);
  return httpClient
    .get(API_GET_STORE_BY_ID.replace('{storeId}', storeId.toString()), DEFAULT_RETRY_CONFIG as AxiosRequestConfig)
    .then((res) => {
      UILogger.log('add store response: ', res.data);
      return res.data;
    })
    .catch((error) => {
      console.log('Error upserting store:::', error.response.data);
      return error.response.data;
    });
};

export const postUpdateStore = (payload: IStore): Promise<IStoreUpdateResp> => {
  UILogger.info('Sending update store request', payload);
  return httpClient
    .post(API_POST_UPSERT_STORE, payload, DEFAULT_RETRY_CONFIG as AxiosRequestConfig)
    .then((res) => {
      UILogger.log('add store response: ', res.data);
      return res.data;
    })
    .catch((error) => {
      console.log('Error upserting store:::', error);
      if (error.status === 403) {
        return { status: { success: false, msg: 'No authorised to update store.' } } as IStoreUpdateResp;
      }
      return error.response.data;
    });
};
export const postDeleteStore = (storeId: number): Promise<ServerResponse> => {
  UILogger.info('Sending deleter store request for store: ', storeId);
  return httpClient
    .delete(API_POST_UPSERT_STORE + storeId, DEFAULT_RETRY_CONFIG as AxiosRequestConfig)
    .then((res) => {
      UILogger.log('delete store response: ', res.data);
      return res.data;
    })
    .catch((error) => {
      console.log('Error deleting store:::', error.response.data);
      return error.response.data;
    });
};

export const getStoreList = async (pageNo: number, pageSize: number): Promise<IStoreListResp> => {
  UILogger.log('StoreService: getting store list: pageNo: {}, pageSize: {}', pageNo, pageSize);
  return httpClient
    .get(API_GET_ALL_STORES + '/' + pageNo + '/' + pageSize)
    .then((res) => {
      UILogger.log('StoreService: Checking storeList, ', res.data);
      if (res.status === 200) {
        return res.data;
      }
      return { status: { success: false } } as AuthResp;
    })
    .catch((error) => {
      if (error?.response?.status !== 401) {
        UILogger.log(
          {
            recordType: LogRecordType.CRASHLYTICS,
            severity: LogSeverityType.ERROR,
            error,
            tag: CRASHLYTICS_AUTH_ERROR,
          },
          'Error in getting stores',
          error,
        );
      }
      UILogger.log('Error in getting stores. status:', error?.response?.status);
      return { status: { success: false } } as AuthResp;
    });
};

export const getStoresForUser = async (userId: number, pageNo: number, pageSize: number): Promise<IStoreListResp> => {
  UILogger.log('StoreService: getting store list: pageNo: {}, pageSize: {}', pageNo, pageSize);
  return httpClient
    .get(
      API_GET_USER_STORES.replace('{userId}', userId.toString())
        .replace('{pageNo}', pageNo.toString())
        .replace('{pageSize}', pageSize.toString()),
    )
    .then((res) => {
      UILogger.log('StoreService: Checking storeList for user, ', res.data);
      if (res.status === 200) {
        return res.data;
      }
      return { status: { success: false } } as AuthResp;
    })
    .catch((error) => {
      if (error?.response?.status !== 401) {
        UILogger.log(
          {
            recordType: LogRecordType.CRASHLYTICS,
            severity: LogSeverityType.ERROR,
            error,
            tag: CRASHLYTICS_AUTH_ERROR,
          },
          'Error in getting stores',
          error,
        );
      }
      UILogger.log('Error in getting stores. status:', error?.response?.status);
      return { status: { success: false } } as AuthResp;
    });
};

export const postSearchStores = async (
  criteria: IStoreSearchCriteria,
  pageNo: number,
  pageSize: number,
): Promise<IStoreListResp> => {
  UILogger.log('StoreService: searching stores:, pageNo:', pageNo, 'pageSize:', pageSize, 'criteria: ', criteria);
  return httpClient
    .post(API_SEARCH_STORES.replace('{pageNo}', pageNo.toString()).replace('{pageSize}', pageSize.toString()), criteria)
    .then((res) => {
      UILogger.log('StoreService: searching stores: done, ', res.data);
      if (res.status === 200) {
        return res.data;
      }
      return { status: { success: false } } as IStoreListResp;
    })
    .catch((error) => {
      if (error?.response?.status !== 401) {
        UILogger.log(
          {
            recordType: LogRecordType.CRASHLYTICS,
            severity: LogSeverityType.ERROR,
            error,
            tag: CRASHLYTICS_AUTH_ERROR,
          },
          'Error in search stores',
          error,
        );
      }
      UILogger.log('Error in search stores. status:', error?.response?.status);
      return { status: { success: false } } as IStoreListResp;
    });
};

export interface IStoreSearchCriteria extends ISearchCriteria {
  franchiseeId?: number;
}
